<template>
  <a-layout class="main-layout">
    <!-- ========== PC 端: 左侧竖导航 ========== -->
    <a-layout-sider
      v-if="!isMobile"
      v-model="collapsed"
      :trigger="null"
      collapsible
      :width="220"
      :collapsed-width="64"
      class="pc-sider"
      theme="dark"
    >
      <!-- Logo -->
      <div class="sider-logo">
        <span class="logo-icon">🌾</span>
        <span v-if="!collapsed" class="logo-text">好粮宝</span>
      </div>

      <!-- 主菜单: 按思维导图 8 个 L1 分组, 每组用 a-sub-menu 折叠 (T-017)
           [2026-09-22] 加 :key 随收缩态强制重建 —— antd-vue 1.7.8 收缩时 switchingModeFromInline
           会把模式卡在 inline (getRealMenuMode), 子菜单在 64px 裁切容器里原地展开导致"点不动";
           重建让菜单直接以 vertical(弹出子菜单) 模式挂载 -->
      <a-menu
        :key="collapsed ? 'collapsed' : 'expanded'"
        mode="inline"
        theme="dark"
        :selected-keys="[$route.name]"
        :default-open-keys="collapsed ? [] : ['grain']"
        @click="onMenuClick"
        class="pc-menu"
      >
        <template v-for="group in visibleGroups">
          <!-- 所有分组统一用 a-sub-menu 折叠 (大菜单 + 子菜单) -->
          <a-sub-menu :key="group.key">
            <span slot="title">
              <a-icon :type="group.icon" />
              <span>{{ group.label }}</span>
            </span>
            <a-menu-item v-for="item in group.visibleItems" :key="item.key" :class="{ 'legacy-item': item.legacy }">
              <a-icon :type="item.icon" />
              <span>{{ item.label }}</span>
              <a-tag v-if="item.legacy" color="#8B6914" class="legacy-tag">原</a-tag>
            </a-menu-item>
          </a-sub-menu>
        </template>
        <!-- [模块开关] 以下入口仅 SUPPER_ADMIN 可见, 不走 menuStructure / perm -->
        <a-menu-item v-if="isSuperAdmin" key="factory-module-assign">
          <a-icon type="deployment-unit" />
          <span>烘干厂分配</span>
        </a-menu-item>
        <!-- [T-151] 外设服务版本管理 — 上传客户端 zip / 发布 / 归档, 现场客户端轮询 manifest 自动更新 -->
        <a-menu-item v-if="isSuperAdmin" key="peripheral-release">
          <a-icon type="cloud-upload" />
          <span>外设服务版本</span>
        </a-menu-item>
        <!-- [2026-09-15] 地磅连接健康 — 各厂地磅(UPort)连接事件总览, 周期评估用, 导出 Markdown 摘要 -->
        <a-menu-item v-if="isSuperAdmin" key="scale-connect-health">
          <a-icon type="dashboard" />
          <span>地磅连接健康</span>
        </a-menu-item>
      </a-menu>

      <!-- Sider 底部: 版本号 -->
      <div v-if="!collapsed" class="sider-footer">
        <div>{{ factoryName }}</div>
        <div class="version">v{{ version }}</div>
      </div>
    </a-layout-sider>

    <!-- ========== 右侧主内容区 ========== -->
    <a-layout class="right-pane">
      <!-- 顶部窄 Header (PC) -->
      <a-layout-header v-if="!isMobile" class="pc-header">
        <div class="header-left">
          <a-icon
            :type="collapsed ? 'menu-unfold' : 'menu-fold'"
            class="trigger"
            @click="toggleSider"
          />
          <span class="page-title">{{ currentPageTitle }}</span>
          <span class="divider">|</span>
          <a-icon type="shop" class="factory-icon" />
          <!-- [超级管理员选厂] 仅 SUPPER_ADMIN (粮易智联超管) 可下拉选厂, 其他人显示厂名 -->
          <a-select
            v-if="isSuperAdmin"
            v-model="selectedFactoryIdProxy"
            :loading="factoryLoading"
            show-search
            option-filter-prop="children"
            placeholder="选择烘干厂"
            :style="{ width: factorySelectWidth + 'px' }"
            @change="onFactorySelect"
          >
            <a-select-option v-for="f in factoryList" :key="f.factoryId" :value="f.factoryId">
              {{ f.factoryName }}
            </a-select-option>
          </a-select>
          <span v-else class="factory-name-header">{{ factoryName || '未关联烘干厂' }}</span>
        </div>

        <div class="header-right">
          <!-- [全局地磅] 常驻顶部, 切页不断开串口连接 -->
          <GlobalScaleBar />

          <!-- 消息铃铛 -->
          <a-tooltip title="通知中心">
            <a-badge :count="noticeUnreadCount" :offset="[-2, 4]" :overflow-count="99">
              <a-icon type="bell" class="header-icon" @click="$router.push('/notice')" />
            </a-badge>
          </a-tooltip>

          <!-- 教程中心 (好粮宝安装/使用/排障、支付宝反向开票等手册, 所有登录用户可见)
               [2026-09-27] 原问号图标辨识度低, 改为醒目文字入口 -->
          <a-tooltip title="好粮宝安装/使用教程、排障手册、支付宝反向开票手册">
            <span class="header-tutorial-btn" @click="$router.push('/tutorial')">教程中心</span>
          </a-tooltip>

          <!-- [2026-09-05] 下载中心: 好粮宝桌面版 + 地磅测试工具;
               [2026-09-11] 桌面容器内也显示 (好粮宝安装包段落会隐藏 — 托盘已有自动更新,
               但地磅测试工具是排障工具, 桌面版用户同样要用) -->
          <a-tooltip title="下载中心">
            <a-icon type="cloud-download" class="header-icon" @click="openWebboxDownload" />
          </a-tooltip>


          <!-- 用户菜单 -->
          <a-dropdown>
            <a class="user-link" @click.prevent>
              <a-avatar size="small" class="user-avatar">
                {{ (displayName || 'U').charAt(0) }}
              </a-avatar>
              <span class="user-name" :title="displayName">{{ displayName }}</span>
              <a-icon type="down" style="font-size: 11px; margin-left: 4px" />
            </a>
            <a-menu slot="overlay" class="user-dropdown">
              <a-menu-item @click="$router.push('/profile')">
                <a-icon type="user" />个人中心
              </a-menu-item>
              <a-menu-item @click="$router.push('/notice')">
                <a-icon type="bell" />我的消息
                <a-badge v-if="noticeUnreadCount" :count="noticeUnreadCount" style="margin-left: 8px" />
              </a-menu-item>
              <!-- [2026-10-05] 导出日志到桌面 — 客户懒得找托盘菜单, 用户菜单里给同款入口
                   (调本机外设服务 8765 打包 zip 放桌面并自动打开文件夹定位) -->
              <a-menu-item :disabled="logExporting" @click="exportPeripheralLogs">
                <a-icon type="file-zip" />导出日志
              </a-menu-item>
              <a-menu-divider />
              <a-menu-item @click="logout" style="color: #f5222d">
                <a-icon type="logout" />退出登录
              </a-menu-item>
            </a-menu>
          </a-dropdown>
        </div>
      </a-layout-header>

      <!-- [2026-05-29] 多页签导航栏 -->
      <tabs-bar />

      <!-- 内容区 -->
      <a-layout-content :class="['main-content', { 'mobile-content': isMobile }]">
        <!-- [2026-05-29] 多页签 keep-alive: 缓存所有已打开页签 (cachedNames), 切页签不刷新, 关页签才销毁.
             首页(入库)始终在 cachedNames 里常驻 -> 海康监控原生窗口不被销毁, 不会跳窗. -->
        <keep-alive :include="cachedNames">
          <router-view />
        </keep-alive>
      </a-layout-content>
    </a-layout>

    <!-- [2026-09-04] UPort 驱动重装弹窗 (全局单例: 地磅多次连接失败大提示 / 驱动管理页触发) -->
    <UPortReinstallModal />

    <!-- [2026-10-05] 外设服务(8765)不可达全局排查弹窗 (单例: 7 处报错点经 peripheralAccessHint 漏斗触发, 30s 节流) -->
    <PeripheralAccessHelperModal />

    <!-- [2026-10-04] 总仓功能升级全局公告 (内容前端写死, 仅对还有「XX默认仓」存量的厂弹出; 随 V3 仓位规则发版) -->
    <WebboxUpdateNotice />

    <!-- [2026-09-22] AI 客服"粮小助"悬浮球+右侧抽屉 (来源: 后端配置表, 兜底 constants/aiHelper.js) -->
    <ai-helper-ball />

    <!-- [2026-09-05] 好粮宝桌面版下载弹窗: 拉 manifest 展示版本/说明, 确认后浏览器直下安装包
         [2026-09-11] 升级为下载中心: 上面常驻「地磅测试工具」静态条目, 下面保留好粮宝 manifest 流程;
         桌面容器内不显示好粮宝安装包段落 (托盘自动更新, 重复下载 157MB 无意义) -->
    <a-modal
      v-model="webboxDownload.visible"
      title="下载中心"
      :width="520"
      :footer="null"
    >
      <!-- 地磅测试工具: 静态条目 (附件服务直链, ShiroConfig 已放行 .exe 匿名下载) -->
      <div class="wb-dl-version">
        <a-icon type="tool" class="wb-dl-version-icon" />
        <span>地磅测试工具</span>
      </div>
      <div class="wb-dl-sub">
        <span>大小：{{ scaleToolSizeText }}</span>
      </div>
      <div class="wb-dl-notes">
        独立小工具，无需安装，下载后双击运行。用于检测地磅串口连接与读数——
        地磅连不上时，先用它排查是地磅/转接线的问题还是系统的问题。
      </div>
      <a-button type="primary" block icon="download" @click="downloadScaleTool">
        立即下载
      </a-button>

      <!-- 好粮宝桌面版: manifest 驱动 (桌面容器内隐藏) -->
      <template v-if="!isDesktop">
        <a-divider style="margin: 20px 0 16px" />
        <a-spin :spinning="webboxDownload.loading">
          <a-alert
            v-if="webboxDownload.error"
            type="error"
            show-icon
            :message="webboxDownload.error"
          />
          <a-alert
            v-else-if="webboxDownload.info && !webboxDownload.info.latestVersion"
            type="warning"
            show-icon
            message="当前没有已发布的安装包，请联系管理员在「外设服务版本」页上传并发布。"
          />
          <template v-else-if="webboxDownload.info">
            <div class="wb-dl-version">
              <a-icon type="cloud-download" class="wb-dl-version-icon" />
              <span>好粮宝桌面版（全量安装包）</span>
            </div>
            <div class="wb-dl-sub">
              <span v-if="webboxDownload.info.latestVersion">最新版本 v{{ webboxDownload.info.latestVersion }}</span>
              <span v-if="webboxDownload.info.publishedAt">发布时间：{{ webboxDownload.info.publishedAt }}</span>
              <span v-if="webboxSizeText">安装包大小：{{ webboxSizeText }}</span>
            </div>
            <div v-if="webboxDownload.info.releaseNotes" class="wb-dl-notes">
              {{ webboxDownload.info.releaseNotes }}
            </div>
            <a-button type="primary" block icon="download" @click="downloadWebboxInstaller">
              下载好粮宝桌面版
            </a-button>
            <p class="wb-dl-tip">
              全量安装包已包含外设服务（地磅/读卡/摄像头）与驱动，下载后双击安装
              （需管理员权限），装完启动好粮宝即自动完成初始化。
            </p>
          </template>
          <div v-else style="min-height: 60px" />
        </a-spin>
      </template>
    </a-modal>

    <!-- ========== 手机端底部 Tab (布局不变) ========== -->
    <div v-if="isMobile" class="mobile-tabbar">
      <div
        v-for="item in mobileTabs"
        :key="item.key"
        :class="['tab-item', { active: $route.name === item.key }]"
        @click="$router.push({ name: item.key })"
      >
        <a-icon :type="item.icon" />
        <span>{{ item.label }}</span>
      </div>
      <div class="tab-item" @click="showMoreMenu = true">
        <a-icon type="appstore" />
        <span>更多</span>
      </div>
    </div>

    <!-- 手机端"更多"抽屉 -->
    <a-drawer
      v-if="isMobile"
      title="更多功能"
      placement="bottom"
      height="60%"
      :visible="showMoreMenu"
      @close="showMoreMenu = false"
    >
      <div class="more-grid">
        <div
          v-for="item in visibleMoreMenuItems"
          :key="item.key"
          class="more-item"
          @click="jumpTo(item.key)"
        >
          <a-icon :type="item.icon" style="font-size: 28px; color: #E9A13B" />
          <div>{{ item.label }}</div>
        </div>
      </div>
    </a-drawer>
  </a-layout>
</template>

<script>
// T-017: 按思维导图 8 个 L1 分组的菜单结构, 单一来源
import { MENU_GROUPS, ALL_ITEMS } from '@/config/menuStructure'
import TabsBar from '@/components/TabsBar.vue'
import GlobalScaleBar from '@/components/GlobalScaleBar.vue'
import UPortReinstallModal from '@/components/UPortReinstallModal.vue'
import PeripheralAccessHelperModal from '@/components/PeripheralAccessHelperModal.vue'
import AiHelperBall from '@/components/aihelper/AiHelperBall.vue'
import WebboxUpdateNotice from '@/components/WebboxUpdateNotice.vue'
import { tabsStore } from '@/store/tabsView'
import { unreadCountNotice } from '@/api/dryingNotice'
import { fetchWebboxManifest } from '@/api/peripheralRelease'
import { clearCredentials } from '@/utils/credentialStore'
import { listAllFactories } from '@/api/dryingFactoryModule'
import PeripheralDebugApi from '@/utils/peripheralDebugApi'
// [2026-10-11] 全局外设服务(8765)在线状态探活: 离线→顶栏红徽标+斩断地磅假已连 (见 peripheralHealth.js)
import { startPeripheralServiceWatch, stopPeripheralServiceWatch } from '@/utils/peripheralHealth'

// 版本号从 package.json 读 (与 Login.vue 一致; 升级只改 package.json 即可)
const APP_VERSION = require('../../package.json').version || '0.0.0'

// [T-128] 通知未读数: 后端 unreadCountNotice 持久化 (替代旧 localStorage+pageCrossEnd 方案)
// [2026-08-29] 轮询停用: 厂内没有公告发布入口, 未读数恒 0, 30s 空轮询纯耗请求.
// 功能整体保留 (API/首次拉取/已读事件/切厂刷新都在), 以后要用把 false 改回 true 即可.
const NOTICE_POLL_ENABLED = false
const NOTICE_POLL_MS = 30 * 1000

// [模块开关] 超管专属路由 — 不在 menuStructure 里, 但 isSuperAdmin 时可直接路由跳转
const SUPER_ONLY_ROUTES = ['factory-module-assign', 'peripheral-release', 'scale-connect-health']

// [2026-09-11] 下载中心·地磅测试工具 — 已上传到后端附件服务 (同教程图片/视频通道),
// ShiroConfig 已放行 .exe 匿名下载, 浏览器裸链可直接下. 换版本时重新上传后改这里的 URL 和大小.
const SCALE_TOOL_URL = '/hl-drying/drying/attachment/static/drying/id-card/20260911/__SCALE_TOOL__.exe'
const SCALE_TOOL_SIZE_BYTES = 13462748

export default {
  name: 'MainLayout',
  components: { TabsBar, GlobalScaleBar, UPortReinstallModal, PeripheralAccessHelperModal, AiHelperBall, WebboxUpdateNotice },
  data() {
    return {
      // 版本号: 读 package.json, 兜底 0.0.0 (require 在构建时内联, 运行时不会变)
      version: APP_VERSION,
      // [2026-09-05] 好粮宝桌面版下载弹窗状态 (info=webbox 通道 manifest result)
      webboxDownload: { visible: false, loading: false, info: null, error: '' },
      isMobile: window.innerWidth < 992,
      // 窄 PC (992~1100px, 如 1042px 低分屏): 默认折叠侧边栏, 给 header 腾出空间
      isNarrowPC: window.innerWidth < 1100,
      collapsed: window.innerWidth < 1100,
      showMoreMenu: false,
      mobileTabs: [
        { key: 'entry',   label: '入库', icon: 'import' },
        { key: 'account', label: '账务', icon: 'account-book' },
        { key: 'payment', label: '付款', icon: 'pay-circle' }
      ],
      moreMenuItems: [
        { key: 'exit',           label: '出库',       icon: 'export' },
        { key: 'seed',           label: '种子',       icon: 'experiment' },
        { key: 'report',         label: '报表',       icon: 'bar-chart' },
        { key: 'notice',         label: '通知',       icon: 'bell' },
        { key: 'user',           label: '用户管理',   icon: 'user' },
        { key: 'role',           label: '角色与权限', icon: 'team' },
        { key: 'dict',           label: '数据字典',   icon: 'profile' },
        { key: 'formula',        label: '干粮换算',   icon: 'calculator' },
        { key: 'quality-price',  label: '质价公式',   icon: 'calculator' },
        { key: 'settings',       label: '设置',       icon: 'setting' },
        { key: 'profile',        label: '个人中心',   icon: 'idcard' }
      ],
      // 跨端通知未读数 (替代旧 mock store getter noticeUnreadCount)
      noticeUnreadCount: 0,
      noticePollTimer: null,
      // [平台超管选厂] DRY_SUPER_ADMIN 未绑定具体厂, 顶部下拉选厂 (选后 SET_EXTERNAL_ORG 全局生效)
      factoryList: [],
      factoryLoading: false,
      // [2026-10-05] 用户菜单「导出日志」进行中标志 (防连点重复打包)
      logExporting: false
    }
  },
  computed: {
    // [2026-05-29] 多页签 keep-alive 缓存名 (已打开页签的组件名)
    cachedNames() { return tabsStore.cachedNames },
    factoryName() { return this.$store.state.factoryName },
    username() { return this.$store.state.username || '操作员' },
    loginUser() { return this.$store.state.loginUser },
    currentUser() { return this.$store.state.currentUser },
    // [模块开关] 是否好粮端超级管理员 (控制「烘干厂分配」菜单可见)
    isSuperAdmin() {
      const lu = this.loginUser || {}
      const codes = Array.isArray(lu.roleCodes) ? lu.roleCodes : []
      return codes.includes('SUPPER_ADMIN')
    },
    // [平台超管选厂] 是否烘干厂平台管理员 (未绑定具体厂, 需顶部选厂). 与 isSuperAdmin(好粮端) 区别.
    isPlatformAdmin() {
      const lu = this.loginUser || {}
      const codes = Array.isArray(lu.roleCodes) ? lu.roleCodes : []
      return codes.includes('DRY_SUPER_ADMIN')
    },
    // [平台超管选厂] 当前选中的厂 id (读 externalOrg.saleOrgId, 与各业务页 factoryId computed 一致)
    selectedFactoryId() {
      const org = this.$store.state.externalOrg || {}
      return org.saleOrgId || ''
    },
    // [平台超管选厂] a-select 的 v-model 代理 (单向: 只用 get, 实际选择由 @change 处理)
    selectedFactoryIdProxy: {
      get() { return this.selectedFactoryId },
      set() { /* 不直接改 store, 由 onFactorySelect 提交 */ }
    },
    // [窄屏] 选厂下拉宽度: 窄屏收窄到 160px 给 header 腾空间, 宽屏保持 220px
    factorySelectWidth() {
      return this.isNarrowPC ? 160 : 220
    },
    displayName() {
      // 优先级: 真实登录用户 userName/realname > mock currentUser.name > username 兜底
      const lu = this.loginUser
      if (lu) return lu.userName || lu.realname || lu.loginAcct || ''
      const cu = this.currentUser
      return cu ? (cu.name || cu.username) : (this.username || '操作员')
    },
    syncStatus() { return this.$store.state.syncStatus },
    // [2026-09-05] 是否运行在好粮宝桌面容器内 (preload contextBridge 暴露; 浏览器里为 false)
    isDesktop() { return !!(window.desktop && window.desktop.isDesktop) },
    // [2026-09-05] 安装包大小的人类可读文本 (manifest size 为字节)
    webboxSizeText() {
      const size = this.webboxDownload.info && this.webboxDownload.info.size
      if (!size || size <= 0) return ''
      const mb = size / 1024 / 1024
      return (mb >= 1024 ? (mb / 1024).toFixed(2) + ' GB' : mb.toFixed(1) + ' MB')
    },
    // [2026-09-11] 地磅测试工具大小 (字节常量 SCALE_TOOL_SIZE_BYTES)
    scaleToolSizeText() {
      const mb = SCALE_TOOL_SIZE_BYTES / 1024 / 1024
      return (mb >= 1024 ? (mb / 1024).toFixed(2) + ' GB' : mb.toFixed(1) + ' MB')
    },
    // 跨端通知未读 factoryId, 用于 refreshNoticeUnread
    noticeFactoryId() {
      const org = this.$store.state.externalOrg || {}
      const u = this.$store.state.loginUser || {}
      return org.saleOrgId || u.externalOrgId || ''
    },

    // T-017: 按用户权限过滤的菜单分组
    permKeys() {
      const keys = this.$store.state.permKeys
      return Array.isArray(keys) ? new Set(keys) : new Set()
    },
    // visibleGroups: 8 个 L1 组, 每组只留有权的 item; 整组没 item 则整组不显示
    // hidden=true 的菜单项不在侧边栏显示 (路由仍保留可直达, 用于废弃但保留入口的页面)
    // superOnly=true 的菜单项仅粮易智联超级管理员 (SUPPER_ADMIN) 可见 (同「外设服务版本」)
    visibleGroups() {
      const keys = this.permKeys
      return MENU_GROUPS
        .map(g => ({
          ...g,
          visibleItems: g.items.filter(it => !it.hidden && (!it.superOnly || this.isSuperAdmin) && (!it.perm || keys.has(it.perm)))
        }))
        .filter(g => g.visibleItems.length > 0)
    },
    visibleMoreMenuItems() {
      // 手机端"更多"抽屉, 排除登录页 + 包含全部有权菜单 + 固定的 notice/profile
      const fixed = ['notice', 'profile']
      const allItems = ALL_ITEMS.filter(it => !it.hidden && (!it.superOnly || this.isSuperAdmin) && (!it.perm || this.permKeys.has(it.perm)))
      const fixedItems = this.moreMenuItems.filter(m => fixed.includes(m.key))
      // [模块开关] 超管额外显示「烘干厂分配」「外设服务版本」「地磅连接健康」入口
      const adminItem = this.isSuperAdmin
        ? [
            { key: 'factory-module-assign', label: '烘干厂分配', icon: 'deployment-unit' },
            { key: 'peripheral-release', label: '外设服务版本', icon: 'cloud-upload' },
            { key: 'scale-connect-health', label: '地磅连接健康', icon: 'dashboard' }
          ]
        : []
      // 去重: 按 key
      const merged = []
      const seen = new Set()
      allItems.concat(fixedItems).concat(adminItem).forEach(it => {
        if (seen.has(it.key)) return
        seen.add(it.key)
        merged.push(it)
      })
      return merged
    },

    currentPageTitle() {
      const route = this.$route
      const found = ALL_ITEMS.find(m => m.key === route.name)
      if (found) return found.label
      const map = {
        notice: '通知中心',
        profile: '个人中心',
        tutorial: '教程中心',
        'tutorial-haoliangbao': '好粮宝使用教程',
        'tutorial-alipay-invoice': '支付宝反向开票手册',
        'tutorial-formula': '质价公式与干粮换算'
      }
      return map[route.name] || (route.meta && route.meta.title) || ''
    }
  },
  mounted() {
    window.addEventListener('resize', this.onResize)
    // [2026-10-11] 外设服务在线探活 (20s 一探; 无外设硬件机器内部跳过). 挂在 MainLayout:
    //   登录后的整个会话期常驻, 与页面切换无关.
    startPeripheralServiceWatch(this.$store)
    // 跨端通知未读数: 首次拉 + 监听 Notice.vue 标已读事件 (30s 轮询已停用, 见文件头 NOTICE_POLL_ENABLED)
    this.refreshNoticeUnread()
    if (NOTICE_POLL_ENABLED) {
      this.noticePollTimer = setInterval(this.refreshNoticeUnread, NOTICE_POLL_MS)
    }
    window.addEventListener('drying-notice-read-changed', this.refreshNoticeUnread)
    // [超级管理员选厂] 仅 SUPPER_ADMIN 顶部下拉选厂; 登录后自动拉厂列表
    if (this.isSuperAdmin) this.loadFactoryList()
    // [2026-05-30] 把全局单例 HikvisionPreviewGrid 暴露给各页面 (入库/出库 抓拍要 captureAll 等接口)
    // [2026-05-30] 海康原生 OS 浮层 z-index 在所有 HTML 之上, modal 罩不住 →
    // 改用 200ms 轮询 (不依赖 MutationObserver, 各种自定义弹窗都能覆盖).
    // 去重: 状态没变就不调 show/hide, 不闪.
  },
  beforeDestroy() {
    stopPeripheralServiceWatch()
    window.removeEventListener('resize', this.onResize)
    if (this.noticePollTimer) { clearInterval(this.noticePollTimer); this.noticePollTimer = null }
    window.removeEventListener('drying-notice-read-changed', this.refreshNoticeUnread)
  },
  watch: {
    // [2026-05-30] portal rect 变了 (页面切换 / 布局抖动) → 让海康原生浮层重新对齐.
    // 用户的反馈是: 切完页面画面位置错乱, 但只要滚一下页面就好 ── 因为 SDK 自己的 scroll 监听里跑了
    //   1) JS_Resize: 重定位浮层屏幕坐标
    //   2) P(): 重算 clip mask (top/bottom/left/right 遮罩, 防浮层超出视口)
    // 只调 I_Resize 只触发 (1) 不触发 (2), 浮层位置变了但 mask 是旧的 → 视频被截一半.
    // 直接派发一个 scroll 事件让 SDK 跑全套.
    noticeFactoryId(v) { if (v) this.refreshNoticeUnread() }
  },
  methods: {
    /** [T-128] 拉后端未读通知数 (总数十按子分类) */
    async refreshNoticeUnread() {
      const fid = this.noticeFactoryId
      if (!fid) { this.noticeUnreadCount = 0; return }
      try {
        const res = await unreadCountNotice(fid)
        this.noticeUnreadCount = (res && res.total) || 0
      } catch (e) { /* 静默轮询失败 */ }
    },
    // ===== [2026-09-05] 好粮宝桌面版下载 =====
    /** 打开下载弹窗并拉最新 manifest (每次打开都重拉: OSS 签名 URL 30 分钟过期).
     *  [2026-09-11] 桌面容器内不拉 manifest — 好粮宝安装包段落不显示, 只留地磅测试工具 */
    async openWebboxDownload() {
      this.webboxDownload = { visible: true, loading: !this.isDesktop, info: null, error: '' }
      if (this.isDesktop) return
      try {
        // 响应拦截器已剥 Result 信封: res 直接就是 manifest VO
        const res = await fetchWebboxManifest()
        this.webboxDownload.info = res || {}
      } catch (e) {
        this.webboxDownload.error = '获取版本信息失败，请稍后重试或联系管理员。'
      } finally {
        this.webboxDownload.loading = false
      }
    },
    /** 触发浏览器下载安装包: [2026-09-18] 统一走后端流代理 (/api 同源 HTTPS) —
     *  OSS 签名 URL 会被部分用户网络劫持重定向到 HTTP, Chrome 按混合内容拦截导致
     *  点下载没反应; 后端从 OSS 内网拉流转发不受影响. 桌面端自动更新仍走签名 URL. */
    downloadWebboxInstaller() {
      const info = this.webboxDownload.info
      if (!info || !info.latestVersion) {
        this.$message.warning('未获取到安装包下载地址，请联系管理员。')
        return
      }
      this.triggerBrowserDownload(
        '/api/hl-drying/drying/peripheral/download/' +
        encodeURIComponent(info.latestVersion) + '?channel=webbox'
      )
    },
    /** [2026-09-11] 下载地磅测试工具: 附件服务相对路径, 拼 /api 前缀 (.exe 已在 ShiroConfig 匿名放行) */
    downloadScaleTool() {
      this.triggerBrowserDownload(
        /^https?:/i.test(SCALE_TOOL_URL) ? SCALE_TOOL_URL : '/api' + SCALE_TOOL_URL
      )
    },
    /** 建临时 <a> 触发浏览器下载 (downloadWebboxInstaller / downloadScaleTool 共用) */
    triggerBrowserDownload(url) {
      const a = document.createElement('a')
      a.href = url
      a.rel = 'noopener'
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
    },
    /** [平台超管选厂] 拉所有烘干厂列表 (复用 factoryManage/list) */
    async loadFactoryList() {
      this.factoryLoading = true
      try {
        const res = await listAllFactories()
        this.factoryList = Array.isArray(res) ? res : (res && res.records ? res.records : [])
      } catch (e) {
        this.factoryList = []
      } finally {
        this.factoryLoading = false
      }
    },
    /** [平台超管选厂] 选中厂 → SET_EXTERNAL_ORG 全局生效 (各业务页 factoryId computed 自动读到) */
    onFactorySelect(factoryId) {
      const f = this.factoryList.find(x => x.factoryId === factoryId)
      if (!f) return
      this.$store.commit('SET_EXTERNAL_ORG', {
        factoryId: f.factoryId,
        factoryName: f.factoryName,
        orgCode: f.orgCode
      })
      this.$message.success(`已切换到: ${f.factoryName}`)
    },
    onResize() {
      const wasMobile = this.isMobile
      this.isNarrowPC = window.innerWidth < 1100
      this.isMobile = window.innerWidth < 992
      // 切到 PC 时, 默认展开 sider
      if (wasMobile && !this.isMobile) this.collapsed = false
    },
    onMenuClick({ key }) {
      // 只接受 ALL_ITEMS 里的 key (排除 group key 这种 submenu 标题)
      if (ALL_ITEMS.find(m => m.key === key)) {
        this.$router.push({ name: key })
        return
      }
      // [模块开关] 超管专属路由, 不在 menuStructure 里但可直接路由
      if (SUPER_ONLY_ROUTES.includes(key)) {
        this.$router.push({ name: key })
      }
    },
    /** 收缩/展开侧边栏 (菜单 :key 随 collapsed 重建, 直接以目标模式挂载) */
    toggleSider() {
      this.collapsed = !this.collapsed
    },
    jumpTo(key) {
      this.showMoreMenu = false
      this.$router.push({ name: key })
    },
    showSyncInfo() {
      this.$info({
        title: '同步状态',
        content: (
          <div>
            <p>上次同步时间: {this.syncStatus.lastSyncTime}</p>
            <p>待同步记录: {this.syncStatus.pendingRecords} 条</p>
            <p>网络状态: {this.syncStatus.networkOk ? '✅ 正常' : '⚠ 异常'}</p>
          </div>
        )
      })
    },
    /** [2026-10-05] 用户菜单「导出日志」— 调本机外设服务(8765)把日志打包 zip 放到桌面,
     *  与托盘「导出日志」同一出口 (含 system-info.txt 电脑现状报告), 完成后自动打开文件夹定位.
     *  未装好粮宝/服务没起来/浏览器拦本地网络时 e.message 带完整指引 (peripheralAccessHint). */
    async exportPeripheralLogs() {
      if (this.logExporting) return
      this.logExporting = true
      const hide = this.$message.loading('正在打包外设服务日志…', 0)
      try {
        const data = await PeripheralDebugApi.exportLogs()
        this.$message.success('日志已导出到桌面：' + ((data && data.zipName) || '好粮宝外设服务日志.zip') + '，可直接发给技术支持', 6)
      } catch (e) {
        this.$message.error((e && e.message) || '导出日志失败，请稍后重试', 8)
      } finally {
        hide()
        this.logExporting = false
      }
    },
    logout() {
      localStorage.removeItem('token')
      // 清掉本地凭证: 避免他人后续打开时用旧凭证自动重登 (安全隐患)
      clearCredentials()
      this.$store.commit('CLEAR_LOGIN')   // T-011: 清掉持久化的登录态 snapshot
      // [2026-08-12] 桌面版：连独立凭据文件里的 token/snapshot 一起清，否则下次启动会被恢复（自动登回）
      try { window.desktop && window.desktop.credentials && window.desktop.credentials.clearToken() } catch (e) { /* ignore */ }
      this.$router.push('/login')
    }
  }
}
</script>

<style lang="less" scoped>
@gold: #E9A13B;
@gold-light: #FFC857;
@gold-dark: #C88424;
@brown: #3E2723;
@brown-soft: #5D4019;
@sider-bg: #2A1A0E;
@sider-bg-hover: #3D2818;

.main-layout {
  height: 100vh;
  overflow: hidden;
}

/* ========== 左侧 Sider ========== */
.pc-sider {
  background: @sider-bg !important;
  box-shadow: 2px 0 8px rgba(0, 0, 0, 0.15);
  position: relative;
  overflow: hidden;

  /deep/ .ant-layout-sider-children {
    display: flex;
    flex-direction: column;
    height: 100vh;
    overflow: hidden;
  }
}

.sider-logo {
  height: 64px;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  padding: 0 20px;
  background: linear-gradient(135deg, @gold 0%, @gold-dark 100%);
  color: #fff;
  font-weight: 600;
  overflow: hidden;
  white-space: nowrap;
  flex-shrink: 0;

  .logo-icon {
    font-size: 28px;
    flex-shrink: 0;
  }

  .logo-text {
    margin-left: 10px;
    font-size: 17px;
    letter-spacing: 1px;
  }
}

.pc-menu {
  flex: 1;
  border-right: none !important;
  background: @sider-bg !important;
  overflow-y: auto;
  overflow-x: hidden;
  padding-top: 8px;

  /* 自定义滚动条: 深色细条, 不遮挡菜单 */
  &::-webkit-scrollbar {
    width: 5px;
  }
  &::-webkit-scrollbar-track {
    background: transparent;
  }
  &::-webkit-scrollbar-thumb {
    background: rgba(255, 255, 255, 0.15);
    border-radius: 3px;
    &:hover {
      background: rgba(255, 255, 255, 0.3);
    }
  }

  /*
   * 修复: a-menu-item 和 a-sub-menu-title 在 inline 模式下顶级 padding 不一致, 图标不在同一竖线
   * antd-vue 1.x 内部对两者用了 inline style 设置 padding-left, 数值微差导致视觉错位.
   * 强制都用 24px (跟内部默认一致), 保证顶级图标对齐.
   */
  /deep/ .ant-menu-inline > .ant-menu-item,
  /deep/ .ant-menu-inline > .ant-menu-submenu > .ant-menu-submenu-title {
    padding-left: 24px !important;
  }

  /deep/ .ant-menu-item {
    height: 46px;
    line-height: 46px;
    margin: 4px 8px;
    border-radius: 6px;
    color: rgba(255, 255, 255, 0.75);
    font-size: 14px;
    width: calc(100% - 16px);

    .anticon {
      font-size: 16px;
    }

    &:hover {
      background: @sider-bg-hover !important;
      color: @gold-light !important;
    }
  }

  /deep/ .ant-menu-item-selected {
    background: linear-gradient(90deg, @gold 0%, @gold-dark 100%) !important;
    color: #fff !important;
    box-shadow: 0 2px 8px rgba(233, 161, 59, 0.4);

    &::after {
      display: none;
    }
  }

  /deep/ .ant-menu-submenu-title {
    height: 46px !important;
    line-height: 46px !important;
    margin: 4px 8px;
    border-radius: 6px;
    color: rgba(255, 255, 255, 0.75);
    width: calc(100% - 16px);

    .anticon {
      font-size: 16px;
    }

    &:hover {
      background: @sider-bg-hover !important;
      color: @gold-light !important;
    }
  }

  /deep/ .ant-menu-submenu-open > .ant-menu-submenu-title {
    color: @gold-light !important;
  }

  /deep/ .ant-menu-sub {
    background: rgba(0, 0, 0, 0.2) !important;
  }

  /deep/ .ant-menu-sub .ant-menu-item {
    padding-left: 48px !important;
    height: 40px;
    line-height: 40px;
  }

  /* T-020: legacy 项的视觉区分 (灰色 + 右侧小 tag) */
  /deep/ .ant-menu-item.legacy-item {
    opacity: 0.7;
    & > span:not(.legacy-tag) {
      color: rgba(255, 255, 255, 0.55) !important;
    }
  }
  /deep/ .legacy-tag {
    font-size: 10px;
    line-height: 16px;
    height: 16px;
    padding: 0 4px;
    margin-left: 6px;
    border: 1px solid rgba(255, 200, 87, 0.3);
    background: rgba(255, 200, 87, 0.12) !important;
    color: #FFC857 !important;
  }

  /* [2026-09-22] 收缩(vertical)态: 图标居中 + 品牌色
     &.ant-menu-vertical 双类限定 (vertical 类在 .pc-menu 自身上), /deep/ 打到内部 title */
  &.ant-menu-vertical /deep/ .ant-menu-submenu-title,
  &.ant-menu-vertical /deep/ .ant-menu-item {
    padding: 0 !important;
    text-align: center;
    width: 100%;
    margin: 4px 0;
    border-radius: 0;
    line-height: 46px;
    height: 46px;

    .anticon {
      margin-right: 0;
      font-size: 18px;
    }
  }
}

.sider-footer {
  padding: 12px 20px;
  font-size: 12px;
  color: rgba(255, 255, 255, 0.5);
  border-top: 1px solid rgba(255, 255, 255, 0.08);
  flex-shrink: 0;

  .version {
    margin-top: 2px;
    color: rgba(255, 255, 255, 0.3);
  }
}

/* ========== 顶部 Header (窄) ========== */
.right-pane {
  background: #f0f2f5;
  overflow: hidden;
  position: relative; /* 作为明细抽屉(getContainer)的定位上下文 → 抽屉从菜单右侧拉出, 不覆盖左侧导航 */
}

.pc-header {
  background: #fff !important;
  height: 56px !important;
  line-height: 56px !important;
  padding: 0 24px !important;
  display: flex;
  justify-content: space-between;
  align-items: center;
  box-shadow: 0 1px 4px rgba(0, 21, 41, 0.06);
  flex-shrink: 0;
  z-index: 9;
}

.header-left {
  display: flex;
  align-items: center;

  .trigger {
    font-size: 18px;
    cursor: pointer;
    padding: 0 12px;
    color: @brown;
    transition: color 0.2s;

    &:hover {
      color: @gold;
    }
  }

  .page-title {
    margin-left: 16px;
    font-size: 16px;
    font-weight: 600;
    color: @brown;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 140px;
  }

  .divider {
    margin: 0 16px;
    color: #d9d9d9;
    font-weight: 300;
    flex-shrink: 0;
  }

  .factory-icon {
    color: @gold;
    margin-right: 6px;
    font-size: 15px;
    flex-shrink: 0;
  }

  .factory-name-header {
    font-size: 14px;
    color: @gold-dark;
    font-weight: 500;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 160px;
  }
}

/* === 窄 PC (低分屏, 如 1042px) header 压缩 ===
 * 地磅条常驻保留, 靠隐藏分隔线/厂图标、缩小间距给 header 腾空间.
 * 宽屏 (≥1100px) 这些规则不触发, 布局完全不受影响.
 */
@media (max-width: 1099px) {
  .pc-header {
    padding: 0 12px !important;
  }

  .header-left {
    .page-title {
      margin-left: 8px;
      max-width: 96px;
    }

    .divider {
      display: none;
    }

    .factory-icon {
      margin-right: 4px;
    }

    .factory-name-header {
      max-width: 120px;
    }
  }

  .header-right {
    gap: 10px;
  }
}

.header-right {
  display: flex;
  align-items: center;
  gap: 18px;
}

.header-icon {
  font-size: 18px;
  color: @brown-soft;
  cursor: pointer;
  transition: color 0.2s;

  &:hover {
    color: @gold;
  }
}

// [2026-09-27] 教程中心文字入口: 原问号图标辨识度低, 换成醒目的四个大字(胶囊描边)
.header-tutorial-btn {
  padding: 3px 14px;
  font-size: 15px;
  font-weight: 600;
  letter-spacing: 1px;
  color: @brown-soft;
  border: 1px solid @brown-soft;
  border-radius: 15px;
  cursor: pointer;
  white-space: nowrap;
  user-select: none;
  transition: all 0.2s;

  &:hover {
    color: @gold;
    border-color: @gold;
    background: fade(@gold, 8%);
  }
}

.user-link {
  display: flex;
  align-items: center;
  color: @brown;
  padding: 4px 8px;
  border-radius: 4px;

  &:hover {
    background: #fafafa;
  }
}

.user-avatar {
  background: linear-gradient(135deg, @gold 0%, @gold-dark 100%) !important;
  color: #fff !important;
  margin-right: 8px;
  font-weight: 500;
}

.user-name {
  font-size: 14px;
  color: @brown;
  display: inline-block;
  max-width: 120px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  vertical-align: middle;
}

.user-dropdown /deep/ .ant-menu-item .anticon {
  margin-right: 8px;
  color: @gold-dark;
}

/* ========== 内容区 ========== */
.main-content {
  background: #f0f2f5;
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;

  /* 自定义滚动条: 浅色细条 */
  &::-webkit-scrollbar {
    width: 6px;
    height: 6px;
  }
  &::-webkit-scrollbar-track {
    background: transparent;
  }
  &::-webkit-scrollbar-thumb {
    background: rgba(0, 0, 0, 0.12);
    border-radius: 3px;
    &:hover {
      background: rgba(0, 0, 0, 0.2);
    }
  }
}

.mobile-content {
  padding-bottom: 60px;
  min-height: 100vh;
}

/* ========== 手机端 (保持原样, 仅替换 active 配色到金黄) ========== */
.mobile-tabbar {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  height: 56px;
  background: #fff;
  border-top: 1px solid #e8e8e8;
  display: flex;
  z-index: 100;
  box-shadow: 0 -2px 8px rgba(0, 0, 0, 0.06);
}

.tab-item {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  font-size: 11px;
  color: #999;
  cursor: pointer;

  .anticon {
    font-size: 20px;
    margin-bottom: 2px;
  }

  &.active {
    color: @gold;
  }
}

.more-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 16px;
}

.more-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 12px;
  font-size: 13px;
  cursor: pointer;

  > div {
    margin-top: 6px;
  }
}

/* === [2026-09-05] 好粮宝桌面版下载弹窗 === */
.wb-dl-version {
  display: flex;
  align-items: center;
  font-size: 17px;
  font-weight: 600;
  color: @gold-dark;

  .wb-dl-version-icon {
    font-size: 22px;
    margin-right: 10px;
    color: @gold;
  }
}

.wb-dl-sub {
  display: flex;
  gap: 24px;
  margin-top: 10px;
  font-size: 13px;
  color: #999;
}

.wb-dl-notes {
  margin: 12px 0 16px;
  padding: 10px 12px;
  background: #fafafa;
  border-radius: 4px;
  font-size: 13px;
  color: #555;
  white-space: pre-wrap;
  word-break: break-word;
  max-height: 180px;
  overflow-y: auto;
}

.wb-dl-tip {
  margin: 12px 0 0;
  font-size: 12px;
  color: #999;
  line-height: 1.7;
}
</style>

<style lang="less">
/* ========== 收缩菜单弹出面板 (挂 body, 必须全局样式; scoped 打不到) ==========
   [2026-09-22] 品牌化: antd 默认 dark 是蓝黑 #001529, 与系统暖棕金主色调不符 */
.ant-menu-submenu-popup.ant-menu-dark,
.ant-menu-submenu-popup.ant-menu-dark .ant-menu {
  background: #2A1A0E !important;
}

.ant-menu-submenu-popup.ant-menu-dark .ant-menu-item {
  color: rgba(255, 255, 255, 0.78);
  border-radius: 6px;
  margin: 4px 8px;
  width: calc(100% - 16px);

  .anticon {
    color: rgba(255, 255, 255, 0.6);
  }

  &:hover {
    background: #3D2818 !important;
    color: #FFC857 !important;
  }
}

.ant-menu-submenu-popup.ant-menu-dark .ant-menu-item-selected {
  background: linear-gradient(90deg, #E9A13B 0%, #C88424 100%) !important;
  color: #fff !important;
}
</style>
